import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import useConfirm from "../../hooks/useConfirm";
import { SettingsService, TaskService, UserService } from "../../services/api";
import {
  Task,
  Ticket,
  Timelog,
  UpdateTaskStatusBodyStatusEnum,
  User,
} from "../../services/swagger";
import FormTemplate from "../templates/FormTemplate";
import useRefresh from "../../hooks/useRefresh";
import FormInput from "../molecules/FormInput";
import { useEffect, useState } from "react";
import { DropDownItem } from "../molecules/Dropdown";
import { useTranslation } from "react-i18next";
import TaskDialogHeader from "../molecules/TaskDialogHeader";
import DateDialog from "./DateDialog";
import TaskTimelogChip from "./TaskTimelogChip";
import ItemList from "../molecules/ItemList";
import AddIcon from "@mui/icons-material/Add";
import TaskProductList from "../molecules/Task/TaskProductList";
import useBreakpoint from "../../hooks/useBreakpoint";

interface TaskDialogProps {
  task: Task;
  ticket?: Ticket;
  open: boolean;
  onClose?: () => void;
}

export default function TaskDialog(props: TaskDialogProps) {
  const { t } = useTranslation("TaskDialog");
  const { task, ticket } = props;
  const [title, setTitle] = useState("");
  const [taskType, setTaskType] = useState("");
  const [taskTypes, setTaskTypes] = useState<DropDownItem[]>([]);
  const [description, setDescription] = useState("");
  const [comment, setComment] = useState("");
  const [assignedUsers, setAssignedUsers] = useState<string[]>([]);
  const [userList, setUserList] = useState<User[]>([]);

  const confirm = useConfirm();
  const refresh = useRefresh();
  const [openDateDialog, setOpenDateDialog] = useState(false);

  const [mode, setMode] = useState<"create" | "edit">("create");
  const [editingTimelog, setEditingTimelog] = useState<Timelog | undefined>();
  const [editingTimelogId, setEditingTimelogId] = useState<string | undefined>(
    "",
  );
  const breakpoint = useBreakpoint();

  const handleOpenDateDialog = (mode: "create" | "edit", logIndex?: number) => {
    setMode(mode);
    if (mode === "edit" && logIndex !== undefined) {
      const log = task.timelog[logIndex];
      setEditingTimelog(log);
      setEditingTimelogId(log?._id || "");
    } else {
      setEditingTimelogId("");
    }
    setOpenDateDialog(true);
  };

  const handleCloseDateDialog = () => {
    setOpenDateDialog(false);
  };

  const handleDateChange = async (startDate: string, endDate?: string) => {
    if (mode === "create") {
      // Call the function to create a new timelog
      await handleAddTimelog(startDate, endDate);
    } else if (mode === "edit" && editingTimelogId) {
      // Call the function to edit an existing timelog
      await handleUpdateTimelog(editingTimelogId, startDate, endDate);
    }
  };

  const handleAddTimelog = async (startDate: string, endDate?: string) => {
    if (!task._id) return;

    const body: any = {
      startDate: new Date(startDate).getTime(),
    };

    if (endDate) {
      body.endDate = new Date(endDate).getTime();
    }

    const res = await TaskService.createTaskTimeLog({
      id: task._id,
      createTaskTimeLogBody: body,
    });

    if (res?.data?.success) {
      refresh();
      handleCloseDateDialog();
    }
  };

  const handleUpdateTimelog = async (
    timelogID: string | undefined,
    startDate: string,
    endDate?: string,
  ) => {
    if (!task._id) return;

    if (!timelogID) return;

    const body: any = {
      startDate: new Date(startDate).getTime(),
    };

    if (endDate) {
      body.endDate = new Date(endDate).getTime();
    }

    const res = await TaskService.updateTaskTimeLog({
      id: task._id, // passed as query param
      timelogId: timelogID,
      updateTaskTimeLogBody: body,
    });

    if (res?.data?.success) {
      refresh(); // reload task to get new timelog
      handleCloseDateDialog(); // close the dialog
    }
  };

  const handleDeleteTimelog = async (index: number) => {
    if (!task._id) return;

    const logToDelete = task.timelog[index];
    if (!logToDelete || !logToDelete._id) return;

    const res = await TaskService.deleteTimeLog({
      id: task._id,
      timelogId: logToDelete._id, // Assuming each timelog has a unique _id
    });

    if (res?.data?.success) {
      refresh();
    }
  };

  const handleAssignUser = async (userToAssign: string) => {
    if (!task._id) return;
    const res = await TaskService.createTaskUser({
      id: task._id,
      createTaskUserBody: {
        userId: userToAssign,
      },
    });
    if (res?.data?.success) {
      refresh();
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!task._id) return;
    const res = await TaskService.deleteTaskUser({
      id: task._id,
      userId,
    });
    if (res?.data?.success) refresh();
  };

  const handleCancel = async () => {
    if (!task._id) return;
    const res = await TaskService.updateTask({
      id: task._id,
      updateTaskBody: {
        cancelled: true,
        comment: task.comment,
        description: task.description,
        title: task.title,
        taskType: task.taskType,
        products: task.products,
      },
    });
    if (res?.data?.success) refresh();
  };

  const handleSubmit = async () => {
    if (!task._id) return;
    const res = await TaskService.updateTask({
      id: task._id,
      updateTaskBody: {
        cancelled: task.cancelled,
        comment: comment,
        description: description,
        title: title,
        taskType: taskType,
        products: task.products,
      },
    });
    if (res?.data?.success) {
      refresh();
      handleClose();
    }
  };

  const handleDeleteTask = async () => {
    if (!task._id) return;
    const res = await TaskService.deleteTask({
      id: task._id,
    });
    if (res?.data?.success) refresh();
  };

  const handleUpdateStatus = async (
    newStatus: UpdateTaskStatusBodyStatusEnum,
  ) => {
    if (!task._id) return;
    const res = await TaskService.updateTaskStatus({
      id: task._id,
      updateTaskStatusBody: {
        status: newStatus,
      },
    });
    if (res?.data?.success) refresh();
  };

  const handleClose = () => {
    props.onClose?.();
  };

  const loadUsers = async () => {
    const res = await UserService.getPrivilegedUserList({
      ignoreDeleted: true,
    });
    if (!res?.data?.data) return;
    if (res?.data?.data) {
      setUserList(res?.data?.data);
    }
  };

  const loadTaskTypes = async () => {
    const res = await SettingsService.getTaskTypes();
    if (!res?.data?.data) return;
    setTaskTypes(res.data.data.map((e) => ({ name: e, value: e })));
  };

  useEffect(() => {
    if (!props.open) return;
    loadUsers();
    loadTaskTypes();
    setTitle(task.title);
    setDescription(task.description);
    setComment(task.comment);
    if (task.taskType) setTaskType(task.taskType);
  }, [props.open]);

  useEffect(() => {
    setAssignedUsers(task.users.map((assignee) => assignee._id || ""));
  }, [ticket]);

  return (
    <Dialog
      PaperProps={{
        sx: { minWidth: { xs: "90%", md: 900, lg: 900, xl: 1000 } },
      }}
      open={props.open}
      onClose={handleClose}
      keepMounted={false}
      fullScreen={!breakpoint.md}
    >
      <Dialog open={openDateDialog} onClose={handleCloseDateDialog}>
        <DateDialog
          onDateChange={handleDateChange}
          onClose={handleCloseDateDialog}
          mode={mode}
          selectedTimelog={editingTimelog}
        />
      </Dialog>

      <TaskDialogHeader
        title={`${task.title}`}
        subtitle={`${new Date(task.date as unknown as string).toLocaleString(
          "tr",
        )}`}
        onCancel={() => confirm(handleCancel)}
        onDelete={() => confirm(handleDeleteTask)}
        onClose={handleClose}
        task={task}
        onStart={() => confirm(() => handleUpdateStatus("start"))}
        onStop={() => confirm(() => handleUpdateStatus("end"))}
      />
      <Divider />
      <DialogContent
        sx={{
          p: 0,
        }}
      >
        <FormTemplate
          title={""}
          confirm
          onSubmit={handleSubmit}
          onCancel={handleClose}
          submitButtonText={t("Save")}
          disableHeader
        >
          <Stack spacing={2}>
            <Stack spacing={2} direction={{ xs: "column", sm: "row" }}>
              <Stack
                width={{ xs: 1, sm: 0.6 }}
                spacing={2}
                direction={"column"}
              >
                <Stack spacing={1} direction="row">
                  <Stack width={{ xs: 1, sm: 0.6 }}>
                    <FormInput
                      field="title"
                      disabled={task.cancelled}
                      value={title}
                      label={t("Title")}
                      onChange={(v) => setTitle(v as string)}
                      type="string"
                    />
                  </Stack>
                  <Stack width={{ xs: 1, sm: 0.4 }}>
                    <FormInput
                      field="tasktype"
                      disabled={task.cancelled}
                      label={t("TaskType")}
                      type="list"
                      onChange={(v) => setTaskType(v as string)}
                      value={taskType}
                      list={taskTypes}
                      placeholder={t("TaskTypePlaceholder")}
                    />
                  </Stack>
                </Stack>

                <FormInput
                  field="description"
                  disabled={task.cancelled}
                  value={description}
                  label={t("Description")}
                  onChange={(v) => setDescription(v as string)}
                  type="string"
                  multiline
                  rows={6}
                />

                <FormInput
                  field="comment"
                  value={comment}
                  label={t("Comments.Label")}
                  onChange={(v) => setComment(v as string)}
                  type="string"
                  placeholder={t("Comments.Placeholder")}
                  multiline
                  rows={4}
                />
              </Stack>

              <Stack width={{ xs: 1, sm: 0.4 }} spacing={1}>
                <Stack spacing={0}>
                  <Button
                    onClick={() => handleOpenDateDialog("create")}
                    variant="text"
                    sx={{ textTransform: "none", mb: 1 }}
                    fullWidth
                    disabled={task.cancelled}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Typography
                        fontWeight={400}
                        fontSize={14}
                        color="primary"
                      >
                        {t("Times")}
                      </Typography>

                      {!task.cancelled && (
                        <AddIcon fontSize={"small"} sx={{ color: "grey" }} />
                      )}
                    </Stack>
                  </Button>

                  {task.timelog && task.timelog.length > 0 ? (
                    <Box
                      sx={{
                        maxHeight: 150,
                        overflowY: "scroll",
                        pr: 1,
                      }}
                    >
                      <Stack spacing={1}>
                        {task.timelog.map((log, index) => (
                          <TaskTimelogChip
                            key={index}
                            timelog={log}
                            task={task}
                            onClick={() => handleOpenDateDialog("edit", index)}
                            onDelete={() =>
                              confirm(() => handleDeleteTimelog(index))
                            }
                          />
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Typography
                      fontSize={11}
                      pl={1}
                      fontWeight={400}
                      color="textSecondary"
                    >
                      {t("NoTimelogYet")}
                    </Typography>
                  )}
                </Stack>

                <Divider />

                <Box p={1} pb={0} pt={0.5}>
                  <ItemList
                    unique
                    onAdd={handleAssignUser}
                    onDelete={handleDeleteUser}
                    disabled={task.cancelled}
                    dragDisabled
                    items={assignedUsers}
                    addList={userList
                      .filter(
                        (user) =>
                          !assignedUsers.includes(user._id as string) &&
                          !user.deleted,
                      )
                      .map((user) => ({
                        name: user.name,
                        value: user._id || "",
                      }))}
                    renderItem={(value) =>
                      userList.find((user) => user._id === value)?.name
                    }
                    emptyLabel={t("NoAssigneesYet")}
                    label={t("Assignees")}
                    confirmAddition
                    confirmDeletion
                  />
                </Box>

                <Divider />
                <TaskProductList task={task} />
              </Stack>
            </Stack>
          </Stack>
        </FormTemplate>
      </DialogContent>
    </Dialog>
  );
}
